import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { GlossaryEmblem } from "@/components/GlossaryEmblem";
import { ShareButton } from "@/components/ShareButton";
import { getGlossaryEntry, relatedEntries, GLOSSARY } from "@/data/glossary";

const SITE = "https://mrbquotes.com";

export const Route = createFileRoute("/glossary/$slug")({
  loader: ({ params }) => {
    const entry = getGlossaryEntry(params.slug);
    if (!entry) throw notFound();
    return { entry };
  },
  head: ({ params, loaderData }) => {
    const e = loaderData?.entry;
    if (!e) {
      return {
        meta: [
          { title: "Glossary term not found — Mr. B Quotes" },
          { name: "robots", content: "noindex" },
        ],
      };
    }
    const url = `${SITE}/glossary/${e.slug}`;
    const title = `${e.term} — Definition, Examples & Usage | Mr. B Quotes Glossary`;
    const desc = `${e.short} In-depth definition with examples and related terms, from the Mr. B Quotes glossary of quotation, attribution, and rhetoric.`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: `${e.term} — Mr. B Quotes Glossary` },
        { property: "og:description", content: e.short },
        { property: "og:url", content: url },
        { property: "og:type", content: "article" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: `${e.term} — Mr. B Quotes Glossary` },
        { name: "twitter:description", content: e.short },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "DefinedTerm",
            name: e.term,
            description: e.short,
            url,
            inDefinedTermSet: { "@type": "DefinedTermSet", name: "Mr. B Quotes Glossary", url: `${SITE}/glossary` },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: `${e.term} — Definition, Examples & Usage`,
            description: e.short,
            articleSection: e.category,
            mainEntityOfPage: url,
            author: { "@type": "Organization", name: "Mr. B Quotes", url: SITE },
            publisher: { "@type": "Organization", name: "Mr. B Quotes", url: SITE },
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SITE },
              { "@type": "ListItem", position: 2, name: "Glossary", item: `${SITE}/glossary` },
              { "@type": "ListItem", position: 3, name: e.term, item: url },
            ],
          }),
        },
      ],
    };
  },
  notFoundComponent: () => (
    <div className="min-h-screen">
      <SiteHeader sub="Quotes · Glossary" active="glossary" />
      <main className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="font-display text-5xl text-gold">Not in the lexicon</h1>
        <p className="mt-4 text-muted-foreground">That term isn't (yet) in the Mr. B Quotes glossary.</p>
        <Link to="/glossary" className="mt-8 inline-block rounded-full border border-border px-5 py-2 text-sm hover:border-gold">Browse all {GLOSSARY.length} terms →</Link>
      </main>
      <SiteFooter />
    </div>
  ),
  component: EntryPage,
});

function EntryPage() {
  const { entry: e } = Route.useLoaderData();
  const related = relatedEntries(e.slug, 4);

  return (
    <div className="min-h-screen">
      <SiteHeader sub="Quotes · Glossary" active="glossary" />

      {/* Banner illustration */}
      <div className="border-b border-border/60">
        <div className="mx-auto max-w-6xl px-4 pt-6">
          <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
            <Link to="/" className="hover:text-gold">Home</Link>
            <span className="mx-2 opacity-50">/</span>
            <Link to="/glossary" className="hover:text-gold">Glossary</Link>
            <span className="mx-2 opacity-50">/</span>
            <span className="text-foreground">{e.term}</span>
          </nav>
        </div>
        <div className="mx-auto max-w-6xl px-4 py-6">
          <div className="overflow-hidden rounded-2xl border border-border bg-card aspect-[5/2] sm:aspect-[16/5]">
            <GlossaryEmblem slug={e.slug} term={e.term} category={e.category} variant="banner" className="h-full w-full" />
          </div>
        </div>
      </div>

      {/* Article */}
      <article className="mx-auto max-w-3xl px-4 py-12 sm:py-16">
        <div className="text-[10px] uppercase tracking-[0.3em] text-gold">{e.category}</div>
        <h1 className="mt-2 font-display text-5xl sm:text-6xl leading-tight">{e.term}</h1>
        <p className="mt-5 text-xl text-muted-foreground leading-relaxed border-l-2 border-gold pl-5 italic">{e.short}</p>

        <div className="prose prose-invert mt-10 max-w-none space-y-5 text-base sm:text-lg text-foreground/90 leading-relaxed">
          {e.body.map((p: string, i: number) => (
            <p key={i}>{p}</p>
          ))}
        </div>

        {e.example && (
          <figure className="mt-10 rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Example</div>
            <blockquote className="mt-3 font-display text-2xl sm:text-3xl leading-snug">&ldquo;{e.example.text}&rdquo;</blockquote>
            <figcaption className="mt-4 text-sm text-gold">
              — {e.example.author}
              {e.example.note && <span className="text-muted-foreground"> · {e.example.note}</span>}
            </figcaption>
          </figure>
        )}

        {/* Related */}
        {related.length > 0 && (
          <section className="mt-14 border-t border-border/60 pt-10">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Related terms</div>
            <h2 className="mt-2 font-display text-2xl">Keep reading the glossary</h2>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to="/glossary/$slug"
                  params={{ slug: r.slug }}
                  className="group rounded-xl border border-border bg-card/40 p-4 transition hover:border-gold"
                >
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{r.category}</div>
                  <div className="mt-1 font-display text-lg text-foreground group-hover:text-gold transition">{r.term}</div>
                  <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{r.short}</p>
                </Link>
              ))}
            </div>
          </section>
        )}

        <div className="mt-12 flex flex-wrap items-center gap-3">
          <ShareButton
            text={`${e.term} — ${e.short}\n\nRead the full definition at mrbquotes.com/glossary/${e.slug}`}
            title={`${e.term} — Mr. B Quotes Glossary`}
            label="Share this term"
          />
          <Link to="/glossary" className="rounded-full border border-border px-5 py-2.5 text-sm hover:border-gold transition">← All glossary terms</Link>
          <Link to="/games" className="rounded-full bg-gold/10 border border-gold/40 px-5 py-2.5 text-sm text-gold hover:bg-gold/15 transition">Play the quote games →</Link>
        </div>
      </article>

      <SiteFooter />
    </div>
  );
}
